import React, { useState, useMemo } from 'react';
import Plot from 'react-plotly.js';
import {
  SENSOR_TYPES, DATE_AXIS_TICK_FORMAT_STOPS, toEasternPlotDate,
  DEPTH_COLORS, FALLBACK_DEPTH_COLORS, COMPARISON_DEPTH_COLORS,
} from '../../../utils/sensorConfig';

const processY = (sensorType, y) => {
  if (sensorType === 'temperature' || sensorType === 'temperature_rdo' || sensorType === 'temperature_rdo_corrected') return y * 9 / 5 + 32;
  if (sensorType === 'o2_25') return y * 0.032;
  if (sensorType === 'pc_fluorometer') return Math.max(0, y);
  return y;
};

// Sensors whose BOTTOM (depth 2) trace is hidden from non-admins by default.
const SURFACE_ONLY_SENSORS = ['pc_fluorometer', 'chla_fluorometer'];

const buildTraces = (transformedData, sensorType, bottomBloomVisible) => {
  const raw = transformedData[sensorType] || [];
  if (!raw.length) return [];

  if (sensorType === 'cyanobacteria_index') {
    const chron = [...raw].reverse();
    return [{
      x: chron.map(r => toEasternPlotDate(r.time)),
      y: chron.map(r => {
        const v = r.sensor_value;
        if (v >= 3) return 4; if (v >= 2) return 3; if (v >= 1) return 2; return 1;
      }),
      type: 'bar',
      marker: { color: raw.map(r => {
        const v = r.sensor_value;
        if (v >= 3) return 'rgba(244,67,54,0.7)'; if (v >= 2) return 'rgba(255,152,0,0.7)';
        if (v >= 1) return 'rgba(255,193,7,0.7)'; return 'rgba(76,175,80,0.7)';
      })},
      hovertemplate: `%{x|%b %d %H:%M}: Risk Index %{customdata}<extra></extra>`,
      customdata: raw.map(r => r.sensor_value.toFixed(1)),
    }];
  }

  const chron = [...raw].reverse();
  const chartData = sensorType === 'wave_height'
    ? chron.map(r => ({ x: toEasternPlotDate(r.time), y: (r.sensor_value || 0) * 100, depth: r.depth }))
    : chron.map(r => ({ x: toEasternPlotDate(r.time), y: r.sensor_value || 0, depth: r.depth }));

  const depth1 = chartData.filter(d => Number(d.depth) === 1);
  const depth2 = chartData.filter(d => Number(d.depth) === 2);
  const c = DEPTH_COLORS[sensorType] || FALLBACK_DEPTH_COLORS;
  const unit = SENSOR_TYPES[sensorType]?.unit || '';
  const traces = [];

  const addTrace = (data, name, color, fill) => {
    if (!data.length) return;
    traces.push({
      x: data.map(d => d.x),
      y: data.map(d => processY(sensorType, d.y)),
      type: 'scatter', mode: 'lines', name,
      line: { color, width: 2 }, fill: 'tozeroy', fillcolor: fill,
      hovertemplate: `${name}<br>%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${unit}<extra></extra>`,
    });
  };

  const showBottomTrace = !SURFACE_ONLY_SENSORS.includes(sensorType) || bottomBloomVisible;
  if (depth1.length || depth2.length) {
    if (showBottomTrace) addTrace(depth2, 'Bottom', c.d2, c.d2fill);
    addTrace(depth1, 'Surface', c.d1, c.d1fill);
  } else {
    const isPoint = sensorType.includes('wind_speed');
    traces.push({
      x: chartData.map(d => d.x),
      y: chartData.map(d => processY(sensorType, d.y)),
      type: 'scatter', mode: isPoint ? 'markers' : 'lines',
      line: isPoint ? undefined : { color: SENSOR_TYPES[sensorType]?.color, width: 2 },
      marker: { color: SENSOR_TYPES[sensorType]?.color, size: 6 },
      name: SENSOR_TYPES[sensorType]?.name,
      hovertemplate: `%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${unit}<extra></extra>`,
    });
  }
  return traces;
};

// A handful of buoys carry a second, Turner phycocyanin sensor that reports as
// `pc`. When it's there, admins see it overlaid on the fluorometer chart for
// side-by-side comparison; everyone else is unaffected.
//
// Like the fluorometer, `pc` reports at both depths (1 = surface, 2 = bottom).
// They must be split into separate traces: concatenating them draws one line
// that jumps between depths, which reads as a spike wherever the bottom sensor
// is running hotter than the surface. Depth stays encoded by dash (dotted =
// Turner) plus its own hue, since the fluorometer's green/navy already sit
// under filled areas that a matching line would disappear into.
const TURNER_PC_DEPTHS = [
  { depth: 1, name: 'PC Turner - Surface', color: COMPARISON_DEPTH_COLORS.d1 },
  { depth: 2, name: 'PC Turner - Bottom',  color: COMPARISON_DEPTH_COLORS.d2 },
];

const buildTurnerPcTrace = (transformedData) => {
  const raw = transformedData.pc || [];
  if (!raw.length) return [];
  const chron = [...raw].reverse();
  const unit = SENSOR_TYPES.pc.unit;

  const mkTrace = (rows, name, color) => ({
    x: rows.map(r => toEasternPlotDate(r.time)),
    y: rows.map(r => r.sensor_value || 0),
    type: 'scatter', mode: 'lines', name,
    line: { color, width: 2, dash: 'dot' },
    hovertemplate: `${name}<br>%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${unit}<extra></extra>`,
  });

  const byDepth = TURNER_PC_DEPTHS
    .map(({ depth, name, color }) => ({ rows: chron.filter(r => Number(r.depth) === depth), name, color }))
    .filter(({ rows }) => rows.length);

  // Buoys that report `pc` without a depth fall back to the original single line.
  if (!byDepth.length) return [mkTrace(chron, 'PC - Turner', COMPARISON_DEPTH_COLORS.d1)];
  return byDepth.map(({ rows, name, color }) => mkTrace(rows, name, color));
};

const PC_LIKE = ['pc', 'chla', 'pc_fluorometer', 'chla_fluorometer'];

// Sensors that legitimately read a flat 0 for long stretches (phycocyanin and
// dissolved oxygen). Give the y-axis a small negative floor so a line sitting
// exactly at 0 is drawn just above the axis baseline instead of vanishing into
// it. chla keeps its 0 floor — it isn't listed here.
const ZERO_FLOOR_SENSORS = ['pc', 'pc_fluorometer', 'o2_25', 'o2_rdo'];

const getYAxisRange = (sensorType, traces) => {
  const isPcLike = PC_LIKE.includes(sensorType);
  const needsFloor = ZERO_FLOOR_SENSORS.includes(sensorType);
  if (!isPcLike && !needsFloor) return undefined;
  const allY = traces.flatMap(t => t.y || []).filter(Number.isFinite);
  const yMax = allY.reduce((a, b) => Math.max(a, b), 0);
  const lower = needsFloor ? -0.5 : 0;
  const upper = isPcLike ? Math.max(10, yMax * 1.1) : Math.max(1, yMax * 1.1);
  return [lower, upper];
};

// Grids where turbidity is exposed to all users (not just admins).
const TURBIDITY_VISIBLE_GRID_IDS = [51];
// Grids where the bottom (depth 2) pc/chla traces are exposed to all users.
const BOTTOM_BLOOM_GRID_IDS = [51];

export default function WaterQualityChart({ transformedData, gridId, isAdmin, sensorOrder, helperTextMap, xRange }) {
  const turbidityVisible = isAdmin || TURBIDITY_VISIBLE_GRID_IDS.includes(Number(gridId));
  const bottomBloomVisible = isAdmin || BOTTOM_BLOOM_GRID_IDS.includes(Number(gridId));
  const showTurnerPc = !!(isAdmin && transformedData.pc?.length && transformedData.pc_fluorometer?.length);
  const available = useMemo(
    () => sensorOrder.filter(t => {
      if (t === 'o2_rdo_saturation' && !isAdmin) return false;
      if (t === 'turbidity_fluorometer' && !turbidityVisible) return false;
      // Turner pc is drawn on the fluorometer chart instead of getting its own chip.
      if (t === 'pc' && showTurnerPc) return false;
      return transformedData[t]?.length;
    }),
    [transformedData, isAdmin, turbidityVisible, showTurnerPc, sensorOrder]
  );

  const [selected, setSelected] = useState(() => available[0] || sensorOrder[0]);

  const activeSensor = available.includes(selected) ? selected : (available[0] || selected);
  const traces = useMemo(() => {
    const base = buildTraces(transformedData, activeSensor, bottomBloomVisible);
    if (showTurnerPc && activeSensor === 'pc_fluorometer') return [...base, ...buildTurnerPcTrace(transformedData)];
    return base;
  }, [transformedData, activeSensor, bottomBloomVisible, showTurnerPc]);
  const yRange = useMemo(() => getYAxisRange(activeSensor, traces), [activeSensor, traces]);

  const st = SENSOR_TYPES[activeSensor] || {};
  const helper = helperTextMap?.[activeSensor];

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.04)', marginBottom: 16 }}>
      {/* Sensor selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: helper ? 10 : 20 }}>
        {available.map(t => (
          <button key={t} onClick={() => setSelected(t)} style={{
            padding: '7px 16px', borderRadius: 100, fontSize: 13, cursor: 'pointer',
            fontWeight: activeSensor === t ? 600 : 400,
            background: activeSensor === t ? '#1e293b' : '#fff',
            color: activeSensor === t ? '#fff' : '#64748b',
            border: activeSensor === t ? 'none' : '1px solid #e2e8f0',
          }}>{SENSOR_TYPES[t]?.name || t}</button>
        ))}
      </div>

      {helper && (
        <p style={{ fontSize: 13, color: '#6f6a5f', lineHeight: 1.5, margin: '0 0 16px' }}>{helper}</p>
      )}

      {traces.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          No {st.name || activeSensor} data available for the selected period.
        </div>
      ) : (
        <Plot
          data={traces}
          layout={{
            height: 350,
            margin: { l: 20, r: 20, t: 10, b: 60 },
            xaxis: { type: 'date', tickformatstops: DATE_AXIS_TICK_FORMAT_STOPS, automargin: true, ...(xRange && { range: xRange }) },
            yaxis: {
              title: { text: `${st.name} (${st.unit})` },
              automargin: true,
              ...(yRange && { range: yRange }),
            },
            showlegend: true,
            legend: { orientation: 'h', x: 0.5, xanchor: 'center', y: -0.2 },
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            hovermode: 'x unified',
          }}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%' }}
        />
      )}
    </div>
  );
}
